import React, { createElement, Fragment, useEffect, useRef } from "react";
import { useSearchBox } from "react-instantsearch-hooks";
import { autocomplete } from "@algolia/autocomplete-js";
import type { BaseItem } from "@algolia/autocomplete-core";
import { useDictionaryContext } from "../../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../../context/site/useSiteContext";
import { DICTIONARY_MAP } from "../../../carlsberg-headless/dictionary-map";
import { createRoot } from "react-dom/client";
import "@algolia/autocomplete-theme-classic";

import type {
  AutocompleteOptions,
  AutocompleteApi,
} from "@algolia/autocomplete-js";

type AutocompleteProps = Partial<AutocompleteOptions<BaseItem>> & {
  onBlur: (autoComplete: AutocompleteApi<BaseItem>) => void;
  onFocus: (autoComplete: AutocompleteApi<BaseItem>) => void;
  onInit: (autoComplete: AutocompleteApi<BaseItem>) => void;
};

// DOCS!
// https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/using-react/

export function Autocomplete(autocompleteProps: AutocompleteProps) {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();

  // Refs to various DOM elements that will be used in the component
  const containerRef = useRef(null);
  const panelRootRef = useRef<HTMLElement | null>(null);
  const panelRootRef2 = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const autoComplete = useRef<AutocompleteApi<BaseItem>>();

  // A ref for the current language of the placeholder label, used to check if it needs to be updated
  const placeholderLabelLanguage = useRef<string>("en");

  // A ref for the current search query
  const currentQuery = useRef<string>();

  // A custom hook that gets the current search query from the Algolia InstantSearch context
  const { query } = useSearchBox();

  // A function that adds event listeners to the input element
  function addInputEvents() {
    inputRef.current = document.querySelector<HTMLInputElement>(".aa-Input");

    inputRef.current?.addEventListener("focus", () => {
      if (autocompleteProps.onFocus && autoComplete.current) {
        autocompleteProps.onFocus(autoComplete.current);
      }
    });

    inputRef.current?.addEventListener("blur", () => {
      if (autocompleteProps.onBlur && autoComplete.current) {
        autocompleteProps.onBlur(autoComplete.current);
      }
    });
  }

  useEffect(() => {
    // If the necessary DOM elements aren't available, return early
    if (!containerRef.current && !panelRootRef2.current) {
      return undefined;
    }

    // Configuration object for the autocomplete instance
    const autocompleteConfig: AutocompleteOptions<BaseItem> = {
      container: containerRef.current!,
      renderer: { createElement, Fragment, render: () => {} },
      panelPlacement: "full-width",
      placeholder:
        get(DICTIONARY_MAP.kiosk.kiosk_placeholder, currentCulture) || "Search",
      panelContainer: panelRootRef2.current!,
      debug: false,
      // A custom render function that uses React to render the suggestions panel
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          //@ts-ignore
          panelRootRef.current = createRoot(root);
        }

        //@ts-ignore
        panelRootRef.current.render(children);
      },
      ...autocompleteProps,
    };

    // If the autocomplete instance hasn't been initialized yet, create it and add event listeners to the input element
    if (
      !autoComplete.current &&
      containerRef.current &&
      panelRootRef2.current
    ) {
      autoComplete.current = autocomplete(autocompleteConfig);
      autocompleteProps.onInit(autoComplete.current);
      addInputEvents();
    }

    // If the autocomplete instance is already initialized, update the query and placeholder label if needed
    if (autoComplete.current) {
      if (currentQuery.current !== query) {
        autoComplete.current.setQuery(query);
        currentQuery.current = query;
      }

      if (placeholderLabelLanguage.current !== currentCulture) {
        inputRef.current =
          document.querySelector<HTMLInputElement>(".aa-Input");

        if (inputRef.current) {
          inputRef.current.placeholder =
            get(DICTIONARY_MAP.kiosk.kiosk_placeholder, currentCulture) ||
            "Search";
        }

        placeholderLabelLanguage.current = currentCulture;
      }
    }

    return () => {};

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCulture, autocompleteProps]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        flex: "1 1 0%",
        fontSize: "h5",
        color: "darkGreen",
        borderWidth: "4px",
        borderRadius: "0px",
      }}
      ref={containerRef}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "-0.25rem",
          right: "-0.25rem",
          zIndex: 10,
          marginTop: "60px",
        }}
        ref={panelRootRef2}
      ></div>
    </div>
  );
}
